/** People (friends/family) endpoints. */

import type { ApiClient } from "./client";

export type Tier = "friend" | "family";

export interface PersonView {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  tier: Tier;
  since: string;
  /**
   * Populated only when the target opted into activity sharing AND
   * the viewer is a connected friend/family. ``null`` otherwise — the
   * UI hides the row entirely in that case so the read-out can't leak
   * the visibility state.
   */
  last_seen_at: string | null;
  last_action_at: string | null;
}

export interface PersonListResponse {
  connections: PersonView[];
}

export interface RequestPartyView {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
}

export interface RequestView {
  id: string;
  other_user: RequestPartyView;
  created_at: string;
}

export interface RequestListResponse {
  incoming: RequestView[];
  outgoing: RequestView[];
}

export interface BlockedView {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  since: string;
}

export interface BlockedListResponse {
  blocked: BlockedView[];
}

export const peopleApi = (client: ApiClient) => ({
  list(tier?: Tier): Promise<PersonListResponse> {
    const q = tier !== undefined ? `?tier=${tier}` : "";
    return client.request<PersonListResponse>(`/people${q}`, { method: "GET" });
  },
  get(userId: string): Promise<PersonView> {
    return client.request<PersonView>(`/people/${userId}`, { method: "GET" });
  },
  updateTier(userId: string, tier: Tier): Promise<PersonView> {
    return client.request<PersonView>(`/people/${userId}`, {
      method: "PATCH",
      json: { tier },
    });
  },
  remove(userId: string): Promise<void> {
    return client.request<void>(`/people/${userId}`, { method: "DELETE" });
  },
  listRequests(): Promise<RequestListResponse> {
    return client.request<RequestListResponse>("/people/requests", { method: "GET" });
  },
  sendRequest(shareCode: string): Promise<RequestView> {
    return client.request<RequestView>("/people/requests", {
      method: "POST",
      json: { share_code: shareCode },
    });
  },
  accept(requestId: string): Promise<PersonView> {
    return client.request<PersonView>(`/people/requests/${requestId}/accept`, {
      method: "POST",
    });
  },
  decline(requestId: string): Promise<void> {
    return client.request<void>(`/people/requests/${requestId}/decline`, {
      method: "POST",
    });
  },
  cancel(requestId: string): Promise<void> {
    return client.request<void>(`/people/requests/${requestId}`, { method: "DELETE" });
  },
  block(userId: string): Promise<void> {
    return client.request<void>(`/people/${userId}/block`, { method: "POST" });
  },
  unblock(userId: string): Promise<void> {
    return client.request<void>(`/people/${userId}/block`, { method: "DELETE" });
  },
  listBlocked(): Promise<BlockedListResponse> {
    return client.request<BlockedListResponse>("/people/blocked", { method: "GET" });
  },
});
